import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Spinner from 'britive-design-system/core/components/spinner'
import Button from 'britive-design-system/core/components/button'

import toast from 'utils/toast'
import AdvancedSettings from 'secret-manager-ui/core/components/advanced-settings/add/AdvancedSettings'
import AdvancedSettingsView from 'secret-manager-ui/core/components/advanced-settings/view/AdvancedSettings'
import { isEmpty, isEqual } from 'lodash'
import {
  fetchProfileSettings,
  createProfileSettings,
  updateProfileSettings,
  fetchAppSettings,
} from 'action_creators/itsm'

export const Settings = ({ match, app, profileId }) => {
  const dispatch = useDispatch()

  const [isView, setView] = useState(true)
  const [settings, setSettings] = useState([
    {
      settingsType: '',
      id: '',
      entityId: '',
      entityType: '',
      isInherited: false,
      isJustificationRequired: false,
      justificationRegex: '',
    },
    {
      settingsType: '',
      id: '',
      entityId: '',
      entityType: '',
      isInherited: false,
      connectionId: '',
      connectionType: '',
      isCommentRequired: false,
      isITSMEnabled: false,
      itsmFilterCriteria: [],
    },
  ])

  const { loading: isLoading, profileSettingsData: settingsData } = useSelector(
    state => state.itsmReducer
  )
  const { appSettingsData: appSettings } = useSelector(state => state.itsmReducer)
  useEffect(() => {
    if (!isEmpty(settingsData)) {
      const newSettings = [...settings] // Copy current state
      // Iterate over the new settings data
      settingsData?.settings?.forEach(setting => {
        if (setting?.settingsType === 'ITSM') {
          newSettings[1] = setting // Add ITSM setting to index 1
        } else if (setting?.settingsType === 'JUSTIFICATION') {
          newSettings[0] = setting // Add JUSTIFICATION setting to index 0
        }
      })
      // Update the state with the modified settings
      setSettings(newSettings)
    } else {
      setSettings([
        {
          settingsType: '',
          id: '',
          entityId: '',
          entityType: '',
          isInherited: false,
          isJustificationRequired: false,
          justificationRegex: '',
        },
        {
          settingsType: '',
          id: '',
          entityId: '',
          entityType: '',
          isInherited: false,
          connectionId: '',
          connectionType: '',
          isCommentRequired: false,
          isITSMEnabled: false,
          itsmFilterCriteria: [],
        },
      ])
    }
  }, [settingsData])

  useEffect(() => {
    fetchSettings()
    getAppSettings()
  }, [app])

  const fetchSettings = () => {
    try {
      dispatch(fetchProfileSettings(profileId))
    } catch (error) {
      const message = error?.response?.data?.message || 'Something went wrong'
      toast({
        title: message,
        type: 'error',
        time: 'normal',
      })
    }
  }

  const getAppSettings = () => {
    try {
      dispatch(fetchAppSettings(app.appContainerId))
    } catch (error) {
      const message = error?.response?.data?.message || 'Something went wrong'
      toast({
        title: message,
        type: 'error',
        time: 'normal',
      })
    }
  }

  // Handle change for JUSTIFICATION settings
  const handleJustificationChange = updatedSetting => {
    setSettings(prevState => [
      { ...prevState[0], ...updatedSetting, entityType: 'PROFILE' }, // Update JUSTIFICATION settings
      prevState[1], // Keep ITSM settings unchanged
    ])
  }

  // Handle change for ITSM settings
  const handleITSMChange = updatedSetting => {
    setSettings(prevState => [
      prevState[0], // Keep JUSTIFICATION settings unchanged
      { ...prevState[1], ...updatedSetting, entityType: 'PROFILE' }, // Update ITSM settings
    ])
  }

  // Justification Handle change for ITSM settings
  const handleResetJustification = () => {
    const justificationSettings = appSettings?.settings?.find(
      setting => setting?.settingsType === 'JUSTIFICATION'
    )

    setSettings(prevState => [
      justificationSettings ?? {
        entityId: '',
        entityType: '',
        id: '',
        isJustificationRequired: false,
        settingsType: '',
      },
      prevState[1], // Keep ITSM settings unchanged
    ])
  }

  // Handle change for Reset ITSM settings
  const handleResetITSM = () => {
    const ITSMSettings = appSettings?.settings?.find(
      setting => setting?.settingsType === 'ITSM'
    )
    setSettings(prevState => [
      prevState[0], // Keep JUSTIFICATION settings unchanged
      ITSMSettings ?? {
        settingsType: '',
        entityType: '',
        connectionId: '',
        connectionType: '',
        isITSMEnabled: false,
        itsmFilterCriteria: [],
      },
    ])
  }
  const showToastMessage = (title, type = 'success') => {
    toast({
      title,
      type,
      time: 'normal',
    })
  }
  // Helper to sanitize settings by removing null or empty settingsType
  const getSanitizedSettings = settings =>
    settings
      ?.filter(Boolean)
      .filter(
        setting =>
          setting?.settingsType !== '' &&
          setting?.isInherited !== true &&
          setting?.entityType === 'PROFILE'
      ) || []

  // Helper to find a setting by its type
  const findSettingByType = (settings, type) =>
    settings?.find(setting => setting?.settingsType === type)

  const handleSave = async () => {
    const justificationParent = findSettingByType(
      appSettings?.settings,
      'JUSTIFICATION'
    )
    const justificationChild = findSettingByType(settings, 'JUSTIFICATION')
    const ITSMParent = findSettingByType(appSettings?.settings, 'ITSM')
    const ITSMChild = findSettingByType(settings, 'ITSM')

    // Map through settings and replace with null if unchanged
    const newSettings = settings?.map((setting, index) => {
      if (index === 0 && isEqual(justificationParent, justificationChild))
        return null
      if (index === 1 && isEqual(ITSMParent, ITSMChild)) return null
      return setting
    })

    // Sanitize settings before saving
    const sanitizedSettings = getSanitizedSettings(newSettings)

    try {
      if (!isEmpty(settingsData)) {
        await dispatch(
          updateProfileSettings(profileId, { settings: sanitizedSettings })
        )
      } else {
        await dispatch(
          createProfileSettings(profileId, { settings: sanitizedSettings })
        )
      }

      toast({
        title: 'Settings saved successfully!',
        type: 'success',
        time: 'normal',
      })
      fetchSettings()
      setView(true)
    } catch (error) {
      const message = error?.response?.data?.message || 'Something went wrong'
      toast({
        title: message,
        type: 'error',
        time: 'normal',
      })
    }
  }
  const cancelHandler = () => {
    setView(true)
    fetchSettings()
  }

  return (
    <>
      {isLoading && <Spinner size="medium" message="Loading" overlay />}
      <div style={{ display: 'flex', gap: '8px', marginLeft: '20px' }}>
        {isView ? (
          <Button size="medium" variant="primary" onClick={() => setView(false)}>
            Edit
          </Button>
        ) : (
          <>
            <Button size="medium" variant="primary" onClick={handleSave}>
              Save
            </Button>
            <Button size="medium" variant="secondary" onClick={cancelHandler}>
              Cancel
            </Button>
          </>
        )}
      </div>
      {isView ? (
        <div style={{ marginLeft: '20px' }}>
          <AdvancedSettingsView settings={settings} />
        </div>
      ) : (
        <div style={{ marginLeft: '20px' }}>
          <AdvancedSettings
            match={match}
            app={app}
            handleResetJustification={handleResetJustification}
            handleResetITSM={handleResetITSM}
            handleJustificationChange={handleJustificationChange}
            handleITSMChange={handleITSMChange}
            settings={settings}
            parentsSettings={appSettings?.settings || {}}
            showToast={showToastMessage}
          />
        </div>
      )}
    </>
  )
}

export default Settings
